import * as Sentry from '@sentry/react';
import { Timestamp, collection, doc, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { uid } from 'uid';

import {
  AssetData,
  ImageAssetData,
  SavedAssetData,
} from '@superfunnel/types/asset-data';

import { db, storage } from '../Firebase';
import {
  compressImageForUpload,
  IMAGE_AUTO_SCALE_THRESHOLD_BYTES,
} from './compressImageForUpload';
import { extractVideoMetadata } from './extractVideoMetadata';
import { removeUndefinedFields } from './firestore';

export type ImageLibraryAssetData = AssetData & {
  wasScaledDown?: boolean;
};

export type UploadedAssetData = SavedAssetData & ImageLibraryAssetData;

interface PreparedUploadFile {
  file: File;
  originalName: string;
  wasScaledDown: boolean;
}

const prepareUploadFile = async (
  originalFile: File,
): Promise<PreparedUploadFile> => {
  const shouldAutoScale =
    originalFile.type.startsWith('image/') &&
    originalFile.size > IMAGE_AUTO_SCALE_THRESHOLD_BYTES;
  const file = shouldAutoScale
    ? await compressImageForUpload(originalFile)
    : originalFile;

  return {
    file,
    originalName: originalFile.name,
    wasScaledDown: shouldAutoScale && file !== originalFile,
  };
};

export const handleUpload = async (
  accountId: string,
  files: File | File[],
  progressCallback?: (progress: number) => void,
  addToLibrary = true,
) => {
  const filesArray = Array.isArray(files) ? files : [files];
  const preparedFiles = await Promise.all(filesArray.map(prepareUploadFile));
  const totalBytes = preparedFiles.reduce(
    (total, { file }) => total + file.size,
    0,
  );
  let bytesTransferred = 0;
  const bytesTransferredMap = new Map<File, number>();

  const updateProgress = () => {
    if (progressCallback) {
      const progress = (bytesTransferred / totalBytes) * 100;
      progressCallback(progress);
    }
  };

  const uploadFile = ({
    file,
    originalName,
    wasScaledDown,
  }: PreparedUploadFile) => {
    return new Promise<UploadedAssetData>((resolve, reject) => {
      const fileType = file.type.startsWith('video') ? 'video' : 'image';

      const fileExt = file.name.split('.').pop();
      const storageRef = ref(
        storage,
        `accounts/${accountId}/uploads/${Date.now()}-${uid(12)}${fileExt ? `.${fileExt}` : ''}`,
      );

      Promise.all([
        extractVideoThumbnailAndUploadIfNeeded(file, accountId),
        fileType === 'video' ? extractVideoMetadata(file) : undefined,
      ])
        .then(([thumbnailImage, videoMetadata]) => {
          const uploadTask = uploadBytesResumable(storageRef, file);

          uploadTask.on(
            'state_changed',
            (snapshot) => {
              bytesTransferredMap.set(file, snapshot.bytesTransferred);
              bytesTransferred = Array.from(
                bytesTransferredMap.values(),
              ).reduce((total, bytes) => total + bytes, 0);
              updateProgress();
            },
            (error) => {
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref,
                );
                const docRef = doc(
                  collection(db, `accounts/${accountId}/assets`),
                );
                const data: SavedAssetData = {
                  id: docRef.id,
                  source: 'storage',
                  url: downloadURL,
                  createdAt: Timestamp.now(),
                  createdBy: '',
                  type: fileType,
                  alt: originalName,
                  name: originalName,
                };

                if (data.type === 'video') {
                  if (thumbnailImage) {
                    data.thumbnailImage = thumbnailImage;
                  }
                  if (videoMetadata) {
                    Object.assign(data, removeUndefinedFields(videoMetadata));
                  }
                }

                if (addToLibrary) {
                  await setDoc(docRef, data);
                }
                resolve(
                  wasScaledDown ? { ...data, wasScaledDown: true } : data,
                );
              } catch (e: any) {
                Sentry.captureException(e);
                reject(e);
              }
            },
          );
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  return Promise.all(preparedFiles.map(uploadFile));
};

export const extractThumbnailFromVideo = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('video/')) {
      return reject(new Error('File is not a video'));
    }

    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('Canvas context not supported'));

    const url = URL.createObjectURL(file);
    video.src = url;
    video.crossOrigin = 'anonymous';
    video.muted = true; // Mute to avoid autoplay restrictions

    video.addEventListener('loadeddata', () => {
      video.currentTime = 1; // Seek to 1 second (adjust as needed)
    });

    video.addEventListener('seeked', () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          const thumbnailFile = new File([blob], 'thumbnail.png', {
            type: 'image/png',
          });
          URL.revokeObjectURL(url);
          resolve(thumbnailFile);
        } else {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to create thumbnail blob'));
        }
      }, 'image/png');
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error loading video'));
    });
  });
};

const extractVideoThumbnailAndUploadIfNeeded = async (
  file: File,
  accountId: string,
  progressCallback?: (progress: number) => void,
): Promise<ImageAssetData | undefined> => {
  if (!file.type.startsWith('video/')) return;

  const thumbnail = await extractThumbnailFromVideo(file);
  const thumbnailData = await handleUpload(
    accountId,
    thumbnail,
    progressCallback,
    false,
  );
  return thumbnailData[0]?.type === 'image' ? thumbnailData[0] : undefined;
};
